// src/features/MediaLibrary/AudioUpload.js - With Cancel button and improvements
import React, { useState, useRef, useCallback, useEffect } from 'react';
import AudioService from './AudioService';
import './AudioUpload.css';

const AudioUpload = ({ onUploadComplete }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Voice/audio specific tag suggestions
  const tagSuggestions = [
    'sarah', 'john', 'maria', 'david',  // Voice talents
    'male', 'female', 'child', 'adult',  // Voice types
    'slow', 'normal', 'fast',  // Speed
    'clear', 'native', 'accent',  // Quality
    'dialogue', 'monologue', 'narrator',  // Style
    'pronunciation', 'conversation', 'reading'  // Content type
  ];

  // Cleanup preview URL when component unmounts or file changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const showMessage = useCallback((text, type = 'info') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 5000);
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      // Check if file is an audio file
      if (!file.type.startsWith('audio/')) {
        showMessage('Please select an audio file.', 'error');
        return;
      }
      
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        showMessage('Audio file size should be less than 10MB', 'error');
        return;
      }
      
      setSelectedFile(file);
      // Set default title from filename (without extension)
      const baseName = file.name.replace(/\.[^/.]+$/, "");
      setTitle(baseName.replace(/[_-]/g, ' '));
      
      // Create preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      
      // Clear any previous messages
      setMessage(null);
    }
  }, [showMessage]);

  const handleTagInput = useCallback((value) => {
    setTags(value);
  }, []);

  const addSuggestedTag = useCallback((tag) => {
    const currentTags = tags.split(',').map(t => t.trim()).filter(t => t !== '');
    if (!currentTags.includes(tag)) {
      const newTags = [...currentTags, tag].join(', ');
      setTags(newTags);
    }
  }, [tags]);

  // ADDED: Clear/Cancel function
  const clearForm = useCallback(() => {
    setSelectedFile(null);
    setTitle('');
    setDescription('');
    setTags('');
    setProgress(0);
    setMessage(null);
    
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [previewUrl]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      showMessage('Please select an audio file to upload.', 'error');
      return;
    }

    if (!title.trim()) {
      showMessage('Please provide a title for the audio file.', 'error');
      return;
    }

    setUploading(true);
    setProgress(0);

    try {
      // Prepare tags array
      const tagsArray = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag !== '');

      // Set up metadata
      const metadata = {
        title: title.trim(),
        description: description.trim() || '',
        tags: tagsArray
      };

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => {
          const increment = Math.floor(Math.random() * 10) + 1;
          return prev + increment > 90 ? 90 : prev + increment;
        });
      }, 300);

      // Upload the file
      const audioAsset = await AudioService.uploadAudio(selectedFile, metadata);
      
      clearInterval(progressInterval);
      setProgress(100);
      
      showMessage('Audio file uploaded successfully!', 'success');
      
      // Reset the form after showing success
      setTimeout(() => {
        if (onUploadComplete) {
          onUploadComplete(audioAsset);
        }
        clearForm();
      }, 1500);
      
    } catch (error) {
      console.error('Error uploading audio:', error);
      showMessage('Failed to upload audio file. Please try again.', 'error');
      setProgress(0);
    } finally {
      setUploading(false);
    }
  }, [selectedFile, title, description, tags, showMessage, onUploadComplete, clearForm]);

  // IMPROVED: File drop handling
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      // Simulate file input change
      const mockEvent = { target: { files: [file] } };
      handleFileChange(mockEvent);
    }
  }, [handleFileChange]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleBrowseClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  return (
    <div className="audio-upload-container">
      {message && (
        <div className={`message-banner ${message.type}`}>
          {message.text}
        </div>
      )}
      
      <div className="upload-instructions">
        <h3>Upload Audio File</h3>
        <p>Select an audio file to upload. Use tags to identify voice talent, speaking style, and content type.</p>
      </div>
      
      <div className="upload-form">
        {/* IMPROVED: File drop area */}
        <div 
          className={`file-drop-area ${selectedFile ? 'has-file' : ''}`}
          onDrop={handleFileDrop}
          onDragOver={handleDragOver}
          onClick={handleBrowseClick}
        >
          <input
            type="file"
            accept="audio/*"
            onChange={handleFileChange}
            ref={fileInputRef}
            disabled={uploading}
            style={{ display: 'none' }}
          />
          
          {selectedFile ? (
            <div className="file-selected">
              <div className="audio-icon">🎵</div>
              <div className="file-info">
                <p><strong>{selectedFile.name}</strong></p>
                <p>{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
              </div>
            </div>
          ) : (
            <div className="drop-message">
              <div className="upload-icon">🎤</div>
              <p>Drag & drop an audio file here or click to browse</p>
              <p className="file-types">Supports MP3, WAV, M4A, OGG (max 10MB)</p>
            </div>
          )}
        </div>
        
        {selectedFile && (
          <div className="selected-file-details">
            <div className="form-row">
              <label htmlFor="title">Title *</label>
              <input
                type="text"
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={uploading}
                placeholder="Enter a title for the audio file"
                required
              />
            </div>

            <div className="form-row">
              <label htmlFor="tags">Tags (comma-separated)</label>
              <input
                type="text"
                id="tags"
                value={tags}
                onChange={(e) => handleTagInput(e.target.value)}
                disabled={uploading}
                placeholder="e.g. sarah, female, slow, pronunciation"
              />
              
              {/* Tag suggestions */}
              <div className="tag-suggestions">
                <small>Quick add: </small>
                {tagSuggestions.slice(0, 8).map(tag => (
                  <button
                    key={tag}
                    type="button"
                    className="tag-suggestion-btn"
                    onClick={() => addSuggestedTag(tag)}
                    disabled={uploading}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="form-row">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={uploading}
                placeholder="Optional description or notes about this audio file"
                rows="3"
              />
            </div>
            
            {/* Preview player */}
            {previewUrl && (
              <div className="preview-container">
                <label>Preview:</label>
                <audio
                  controls
                  src={previewUrl}
                  className="audio-preview"
                  style={{
                    width: '100%',
                    marginTop: '5px'
                  }}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Progress bar */}
        {uploading && (
          <div className="upload-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="progress-text">
              {progress < 100 ? `Uploading... ${progress}%` : 'Processing...'}
            </div>
          </div>
        )}
        
        {/* IMPROVED: Form actions with Cancel button */}
        <div className="form-actions">
          <button
            onClick={clearForm}
            disabled={uploading || !selectedFile}
            className="clear-button"
          >
            Clear
          </button>
          
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading || !title.trim()}
            className="upload-button"
          >
            {uploading ? 'Uploading...' : 'Upload Audio'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AudioUpload);